import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Notifications from 'expo-notifications';
import { apiPost } from './apiClient';

const EXPO_PROJECT_ID = 'fd6b1952-fe38-4419-a38f-ad576d509e70';

/**
 * A person's "Push Notifications: off" on this phone. Switching push off in
 * Settings leaves the OS permission granted, so the permission can't carry the
 * choice: the sign-in registration below used to turn push straight back on.
 * Per user, so someone else signing in on the same phone keeps their own.
 */
const optOutKey = (cognitoId: string) => `@eleplan/push_opted_out:${cognitoId}`;

export async function isPushOptedOut(cognitoId: string | null | undefined): Promise<boolean> {
  if (!cognitoId) return false;
  try {
    return (await AsyncStorage.getItem(optOutKey(cognitoId))) === 'true';
  } catch {
    return false;
  }
}

export async function setPushOptedOut(cognitoId: string, optedOut: boolean): Promise<void> {
  try {
    if (optedOut) await AsyncStorage.setItem(optOutKey(cognitoId), 'true');
    else await AsyncStorage.removeItem(optOutKey(cognitoId));
  } catch {
    // Best effort: the server subscription was already updated by the caller.
  }
}

/**
 * Registers this phone's Expo push token at sign-in and session restore, so a
 * permission granted in an earlier session (during onboarding, say) reaches the
 * server without a visit to Settings. Skips when the OS permission isn't
 * granted or the person switched push off in the app.
 *
 * No `Constants.isDevice` guard: expo-constants dropped it in SDK 50+, and the
 * undefined value made this bail on every real device. getExpoPushTokenAsync
 * can throw (no network, or a build without the push entitlement); callers
 * catch it. An iOS simulator build that carries aps-environment does get a
 * real token.
 */
export async function autoRegisterPushToken(accountId: string, cognitoId: string): Promise<void> {
  const { status } = await Notifications.getPermissionsAsync();
  if (status !== 'granted') return;
  if (await isPushOptedOut(cognitoId)) return;
  const tokenData = await Notifications.getExpoPushTokenAsync({ projectId: EXPO_PROJECT_ID });
  if (!tokenData?.data) return;
  await apiPost(`/users/register-expo-token/${accountId}/${cognitoId}`, {
    token: tokenData.data,
    active: true,
  });
}
